import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  loadReportingKpiTargetsForScope,
  saveReportingKpiTargetsForScope,
  getReportingKpiTargetBasis,
  saveReportingKpiTargetBasis,
  DEFAULT_REPORTING_KPI_TARGETS,
  type ReportingKpiTargets,
  type ReportingKpiScope,
  type ReportingKpiTargetBasis,
} from '../services/reportingKpiTargets';
import {
  loadReportingBranches,
  saveReportingBranches,
  addReportingBranch,
  updateReportingBranch,
  deleteReportingBranch,
  type ReportingBranch,
  type ReportingBranchKind,
  reportingBranchesStorageEvent,
} from '../services/reportingBranches';
import { supabase } from '../services/supabaseService';
import {
  Target,
  RotateCcw,
  Save,
  BarChart2,
  GitBranch,
  Plus,
  Pencil,
  Trash2,
  X,
  Building2,
  User,
  Network,
} from 'lucide-react';

interface ReportingKpiSettingsProps {
  user: any;
  clients: any[];
}

const FIELDS: { key: keyof ReportingKpiTargets; label: string; hint?: string }[] = [
  { key: 'jobsAdded', label: 'Vacatures toegevoegd (periode)' },
  { key: 'shortlistsAdded', label: 'Shortlists toegevoegd' },
  { key: 'cvsSent', label: 'CV’s verstuurd' },
  { key: 'firstInterviews', label: 'Eerste gesprekken' },
  { key: 'furtherInterviews', label: 'Vervolggesprekken' },
  { key: 'totalInterviews', label: 'Totaal gesprekken (doel)' },
  { key: 'offers', label: 'Aanbiedingen' },
  { key: 'permPlacements', label: 'Perm-plaatsingen' },
  {
    key: 'websiteApplications',
    label: 'Website-sollicitaties',
    hint: 'Career site / werkenbij — zie rapportage-tegel',
  },
  { key: 'newCompanies', label: 'Nieuwe bedrijven (activiteit)', hint: 'Optioneel — voor activiteitssamenvatting' },
];

type ScopeMode = 'default' | 'client' | 'recruiter' | 'organization';

function scopeFromUi(mode: ScopeMode, entityId: string): ReportingKpiScope {
  if (mode === 'default') return { kind: 'default' };
  if (mode === 'client') return { kind: 'client', id: entityId };
  if (mode === 'recruiter') return { kind: 'recruiter', id: entityId };
  return { kind: 'organization', id: entityId };
}

const ReportingKpiSettings: React.FC<ReportingKpiSettingsProps> = ({ user, clients }) => {
  const tenantId = user?.clientId || user?.recruiterProfile?.clientId || null;

  const [scopeMode, setScopeMode] = useState<ScopeMode>('default');
  const [scopeEntityId, setScopeEntityId] = useState('');
  const [form, setForm] = useState<ReportingKpiTargets>(() =>
    loadReportingKpiTargetsForScope(tenantId, { kind: 'default' })
  );
  const [saved, setSaved] = useState(false);
  const [targetBasis, setTargetBasis] = useState<ReportingKpiTargetBasis>(() =>
    getReportingKpiTargetBasis(tenantId)
  );

  const [recruiters, setRecruiters] = useState<{ id: string; name: string }[]>([]);
  const [branches, setBranches] = useState<ReportingBranch[]>(() => loadReportingBranches(tenantId));

  const [branchModal, setBranchModal] = useState<ReportingBranch | null>(null);
  const [branchDraft, setBranchDraft] = useState<{
    name: string;
    kind: ReportingBranchKind;
    memberIds: string[];
  }>({ name: '', kind: 'clients', memberIds: [] });

  const rootOrganizations = useMemo(
    () =>
      (clients || []).filter((c) => !c.parent_client_id).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'nl')),
    [clients]
  );

  const sortedClients = useMemo(
    () => [...(clients || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'nl')),
    [clients]
  );

  const activeScope: ReportingKpiScope = useMemo(() => {
    if (scopeMode === 'default') return { kind: 'default' };
    if (!scopeEntityId) return { kind: 'default' };
    return scopeFromUi(scopeMode, scopeEntityId);
  }, [scopeMode, scopeEntityId]);

  useEffect(() => {
    setForm(loadReportingKpiTargetsForScope(tenantId, activeScope));
  }, [tenantId, activeScope]);

  useEffect(() => {
    setTargetBasis(getReportingKpiTargetBasis(tenantId));
  }, [tenantId]);

  useEffect(() => {
    const refreshBasis = () => setTargetBasis(getReportingKpiTargetBasis(tenantId));
    window.addEventListener('werftools-reporting-kpi-saved', refreshBasis);
    window.addEventListener('storage', refreshBasis);
    return () => {
      window.removeEventListener('werftools-reporting-kpi-saved', refreshBasis);
      window.removeEventListener('storage', refreshBasis);
    };
  }, [tenantId]);

  useEffect(() => {
    const loadRecruiters = async () => {
      const { data } = await supabase
        .from('profiles')
        .select('id,name,email')
        .in('role', ['RECRUITER', 'ADMIN', 'CLIENT_RECRUITER'])
        .order('name');
      setRecruiters((data || []).map((r: any) => ({ id: r.id, name: r.name || r.email || r.id })));
    };
    loadRecruiters();
  }, []);

  const refreshBranches = useCallback(() => {
    setBranches(loadReportingBranches(tenantId));
  }, [tenantId]);

  useEffect(() => {
    refreshBranches();
    const ev = reportingBranchesStorageEvent();
    const onStorage = () => refreshBranches();
    window.addEventListener(ev, onStorage);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(ev, onStorage);
      window.removeEventListener('storage', onStorage);
    };
  }, [refreshBranches]);

  useEffect(() => {
    setScopeMode('default');
    setScopeEntityId('');
  }, [tenantId]);

  const update = (key: keyof ReportingKpiTargets, raw: string) => {
    const n = parseFloat(String(raw || '').replace(',', '.'));
    const rounded = Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
    setForm((f) => ({ ...f, [key]: rounded }));
    setSaved(false);
  };

  const handleSaveKpi = () => {
    saveReportingKpiTargetsForScope(tenantId, activeScope, form);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleResetKpi = () => {
    if (!confirm('Doelen voor deze scope terugzetten naar standaard?')) return;
    const reset = { ...DEFAULT_REPORTING_KPI_TARGETS };
    setForm(reset);
    saveReportingKpiTargetsForScope(tenantId, activeScope, reset);
    setSaved(true);
  };

  const openNewBranch = () => {
    setBranchDraft({ name: '', kind: 'clients', memberIds: [] });
    setBranchModal({ id: '__new__', name: '', kind: 'clients', memberIds: [] });
  };

  const openEditBranch = (b: ReportingBranch) => {
    setBranchDraft({ name: b.name, kind: b.kind, memberIds: [...b.memberIds] });
    setBranchModal(b);
  };

  const saveBranchModal = () => {
    const name = branchDraft.name.trim();
    if (!name) {
      alert('Geef een naam voor de branch.');
      return;
    }
    if (branchDraft.memberIds.length === 0) {
      alert('Selecteer minstens één bedrijf of recruiter.');
      return;
    }
    if (branchModal?.id === '__new__') {
      addReportingBranch(tenantId, {
        name,
        kind: branchDraft.kind,
        memberIds: branchDraft.memberIds,
      });
    } else if (branchModal) {
      updateReportingBranch(tenantId, branchModal.id, {
        name,
        kind: branchDraft.kind,
        memberIds: branchDraft.memberIds,
      });
    }
    setBranchModal(null);
    refreshBranches();
  };

  const toggleMember = (id: string) => {
    setBranchDraft((d) => ({
      ...d,
      memberIds: d.memberIds.includes(id) ? d.memberIds.filter((x) => x !== id) : [...d.memberIds, id],
    }));
  };

  const scopeNeedsEntity = scopeMode !== 'default';
  const scopeBlocked = scopeNeedsEntity && !scopeEntityId;

  return (
    <div className="h-full w-full overflow-y-auto bg-gradient-to-br from-slate-50 via-white to-red-50/25 p-4 md:p-6 lg:p-8">
      <div className="w-full max-w-none space-y-10">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#e51b23] to-red-700 shadow-lg shadow-red-500/25">
            <BarChart2 className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-black tracking-tight text-slate-900">KPI-doelen — Rapportage</h1>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              Stel doelen in voor de gauges op het rapportagedashboard. Je kunt dit{' '}
              <strong className="font-semibold text-slate-800">per Werftools-klant (tenant)</strong>,{' '}
              <strong className="font-semibold text-slate-800">per bedrijf</strong>,{' '}
              <strong className="font-semibold text-slate-800">per recruiter</strong> of{' '}
              <strong className="font-semibold text-slate-800">per moederbedrijf</strong> (organisatie-root). In rapportage
              worden automatisch de meest specifieke doelen gekozen op basis van je filters (bedrijf → recruiter →
              organisatie → standaard).
            </p>
          </div>
        </div>

        {/* KPI scope + values */}
        <div className="rounded-2xl border border-slate-200/80 bg-white/90 p-6 shadow-xl shadow-slate-200/50 backdrop-blur-md">
          <div className="mb-6 flex flex-wrap items-center gap-2 text-slate-700">
            <Target className="h-5 w-5 text-[#e51b23]" />
            <h2 className="text-sm font-bold uppercase tracking-wide text-slate-600">Doelen per metric</h2>
          </div>

          <div className="mb-6 space-y-3 rounded-xl border border-slate-100 bg-slate-50/80 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Scope</p>
            <div className="grid w-full grid-cols-2 gap-2 sm:grid-cols-2 md:grid-cols-4">
              {(
                [
                  { id: 'default' as const, label: 'Hele tenant', icon: Network },
                  { id: 'client' as const, label: 'Bedrijf', icon: Building2 },
                  { id: 'recruiter' as const, label: 'Recruiter', icon: User },
                  { id: 'organization' as const, label: 'Moederbedrijf', icon: Building2 },
                ] as const
              ).map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => {
                    setScopeMode(id);
                    setScopeEntityId('');
                    setSaved(false);
                  }}
                  className={`inline-flex w-full min-w-0 items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-xs font-bold transition ${
                    scopeMode === id
                      ? 'border-[#e51b23] bg-red-50 text-red-900'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                  }`}
                >
                  <Icon size={14} className="shrink-0" /> <span className="truncate">{label}</span>
                </button>
              ))}
            </div>

            {scopeMode === 'client' && (
              <select
                value={scopeEntityId}
                onChange={(e) => setScopeEntityId(e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-2 focus:ring-red-100"
              >
                <option value="">— Kies bedrijf —</option>
                {sortedClients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name || c.id}
                  </option>
                ))}
              </select>
            )}
            {scopeMode === 'recruiter' && (
              <select
                value={scopeEntityId}
                onChange={(e) => setScopeEntityId(e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-2 focus:ring-red-100"
              >
                <option value="">— Kies recruiter —</option>
                {recruiters.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            )}
            {scopeMode === 'organization' && (
              <select
                value={scopeEntityId}
                onChange={(e) => setScopeEntityId(e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-2 focus:ring-red-100"
              >
                <option value="">— Kies moederbedrijf (organisatie) —</option>
                {rootOrganizations.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name || c.id}
                  </option>
                ))}
              </select>
            )}
            {scopeBlocked && (
              <p className="text-xs text-amber-700">Selecteer een entiteit om doelen voor deze scope te bewerken.</p>
            )}
          </div>

          <div className="mb-6 space-y-2 rounded-xl border border-red-100 bg-red-50/50 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-red-900">
              Doelen zijn bedoeld per (dag / week / maand / jaar)
            </p>
            <p className="text-xs text-slate-600">
              De ingevulde getallen gelden voor één dag, week, maand of jaar — afhankelijk van je keuze. Op het
              rapportagedashboard worden ze automatisch omgezet naar de gekozen filterperiode (bijv. 30 dagen).
            </p>
            <select
              value={targetBasis}
              onChange={(e) => {
                const v = e.target.value as ReportingKpiTargetBasis;
                setTargetBasis(v);
                saveReportingKpiTargetBasis(tenantId, v);
                // Basis switch converts & persists all scopes in storage; reload the active scope so
                // the user sees the updated numbers immediately.
                setForm(loadReportingKpiTargetsForScope(tenantId, activeScope));
              }}
              className="mt-1 w-full rounded-xl border border-red-200 bg-white px-3 py-2 text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-2 focus:ring-red-100"
            >
              <option value="day">Per dag</option>
              <option value="week">Per week</option>
              <option value="month">Per maand</option>
              <option value="year">Per jaar</option>
            </select>
          </div>

          <div className={`space-y-4 ${scopeBlocked ? 'pointer-events-none opacity-40' : ''}`}>
            {FIELDS.map(({ key, label, hint }) => (
              <div
                key={key}
                className="grid grid-cols-1 gap-2 border-b border-slate-50 pb-4 last:border-0 last:pb-0 lg:grid-cols-[minmax(0,1fr)_minmax(120px,420px)] lg:items-center lg:gap-8"
              >
                <label className="text-sm font-semibold text-slate-700">
                  {label}
                  {hint && (
                    <span className="mt-0.5 block text-[11px] font-normal text-slate-400">{hint}</span>
                  )}
                </label>
                <input
                  type="number"
                  min={0}
                  step={key === 'newCompanies' ? 1 : 0.01}
                  value={typeof form[key] === 'number' && Number.isFinite(form[key] as number) ? String(form[key] as number) : ''}
                  onChange={(e) => update(key, e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 shadow-inner outline-none transition focus:border-[#e51b23] focus:ring-2 focus:ring-red-100 sm:max-w-[240px] md:max-w-[320px] lg:max-w-[400px]"
                />
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-slate-100 pt-6">
            <button
              type="button"
              onClick={handleSaveKpi}
              disabled={scopeBlocked}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#e51b23] to-red-700 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-red-500/30 transition hover:brightness-110 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Save size={16} /> Opslaan
            </button>
            <button
              type="button"
              onClick={handleResetKpi}
              disabled={scopeBlocked}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-100 disabled:opacity-40"
            >
              <RotateCcw size={16} /> Standaardwaarden
            </button>
            {saved && <span className="text-sm font-semibold text-emerald-600">Opgeslagen.</span>}
          </div>
        </div>

        {/* Branches */}
        <div className="rounded-2xl border border-red-200/80 bg-white/90 p-6 shadow-xl shadow-red-100/40 backdrop-blur-md">
          <div className="mb-2 flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-slate-800">
                <GitBranch className="h-5 w-5 text-[#e51b23]" />
                <h2 className="text-sm font-bold uppercase tracking-wide text-slate-600">Branches (alleen rapportage)</h2>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Voeg meerdere <strong className="font-semibold text-slate-800">bedrijven</strong> of{' '}
                <strong className="font-semibold text-slate-800">recruiters</strong> samen onder één eigen naam. Dat is
                alleen zichtbaar in rapportage-filters; het wijzigt geen data in het ATS.
              </p>
            </div>
            <button
              type="button"
              onClick={openNewBranch}
              className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-[#e51b23] px-4 py-2 text-xs font-bold text-white shadow-md transition hover:bg-red-700"
            >
              <Plus size={14} /> Nieuwe branch
            </button>
          </div>

          <ul className="mt-4 divide-y divide-slate-100 rounded-xl border border-slate-100 bg-slate-50/50">
            {branches.length === 0 ? (
              <li className="px-4 py-8 text-center text-sm text-slate-500">Nog geen branches. Maak er een om filters te bundelen.</li>
            ) : (
              branches.map((b) => (
                <li key={b.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
                  <div>
                    <span className="font-bold text-slate-900">{b.name}</span>
                    <span className="ml-2 rounded-md bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase text-red-800">
                      {b.kind === 'clients' ? 'Bedrijven' : 'Recruiters'}
                    </span>
                    <span className="ml-2 text-xs text-slate-500">
                      {b.memberIds.length} {b.kind === 'clients' ? 'bedrijven' : 'recruiters'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => openEditBranch(b)}
                      className="rounded-lg p-2 text-slate-500 hover:bg-white hover:text-[#e51b23]"
                      title="Bewerken"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (confirm(`Branch “${b.name}” verwijderen?`)) {
                          deleteReportingBranch(tenantId, b.id);
                          refreshBranches();
                        }
                      }}
                      className="rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-600"
                      title="Verwijderen"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>
      </div>

      {/* Branch modal */}
      {branchModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-4" role="dialog">
          <div className="max-h-[90vh] w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <h3 className="text-lg font-bold text-slate-900">
                {branchModal.id === '__new__' ? 'Nieuwe branch' : 'Branch bewerken'}
              </h3>
              <button
                type="button"
                onClick={() => setBranchModal(null)}
                className="rounded-lg p-2 text-slate-400 hover:bg-slate-100"
              >
                <X size={20} />
              </button>
            </div>
            <div className="max-h-[calc(90vh-140px)] overflow-y-auto px-5 py-4">
              <label className="block text-xs font-bold uppercase text-slate-500">Naam</label>
              <input
                value={branchDraft.name}
                onChange={(e) => setBranchDraft((d) => ({ ...d, name: e.target.value }))}
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold"
                placeholder="Bijv. Regio Zuid"
              />
              <label className="mt-4 block text-xs font-bold uppercase text-slate-500">Type</label>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setBranchDraft((d) => ({
                      ...d,
                      kind: 'clients',
                      memberIds: d.kind === 'clients' ? d.memberIds : [],
                    }))
                  }
                  className={`flex-1 rounded-xl border px-3 py-2 text-xs font-bold ${
                    branchDraft.kind === 'clients'
                      ? 'border-[#e51b23] bg-red-50 text-red-900'
                      : 'border-slate-200 bg-white text-slate-600'
                  }`}
                >
                  Bedrijven samenvoegen
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setBranchDraft((d) => ({
                      ...d,
                      kind: 'recruiters',
                      memberIds: d.kind === 'recruiters' ? d.memberIds : [],
                    }))
                  }
                  className={`flex-1 rounded-xl border px-3 py-2 text-xs font-bold ${
                    branchDraft.kind === 'recruiters'
                      ? 'border-[#e51b23] bg-red-50 text-red-900'
                      : 'border-slate-200 bg-white text-slate-600'
                  }`}
                >
                  Recruiters samenvoegen
                </button>
              </div>
              <p className="mt-3 text-xs text-slate-500">
                Vink members aan die bij deze branch horen (én hetzelfde type blijven gebruiken).
              </p>
              <div className="mt-3 max-h-52 space-y-1 overflow-y-auto rounded-xl border border-slate-100 bg-slate-50 p-2">
                {branchDraft.kind === 'clients'
                  ? sortedClients.map((c) => (
                      <label
                        key={c.id}
                        className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-white"
                      >
                        <input
                          type="checkbox"
                          checked={branchDraft.memberIds.includes(c.id)}
                          onChange={() => toggleMember(c.id)}
                          className="rounded border-slate-300 text-[#e51b23]"
                        />
                        <span className="truncate">{c.name || c.id}</span>
                      </label>
                    ))
                  : recruiters.map((r) => (
                      <label
                        key={r.id}
                        className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-white"
                      >
                        <input
                          type="checkbox"
                          checked={branchDraft.memberIds.includes(r.id)}
                          onChange={() => toggleMember(r.id)}
                          className="rounded border-slate-300 text-[#e51b23]"
                        />
                        <span className="truncate">{r.name}</span>
                      </label>
                    ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-4">
              <button
                type="button"
                onClick={() => setBranchModal(null)}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={saveBranchModal}
                className="rounded-xl bg-[#e51b23] px-5 py-2 text-sm font-bold text-white hover:bg-red-700"
              >
                Opslaan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportingKpiSettings;
